import React, { ReactNode, useContext, useMemo } from 'react'
import LazyLoad from 'react-lazyload'

import LinesEllipsisLoose from 'react-lines-ellipsis/lib/loose'

import ProductLabelInventory from 'components/ProductLabelInventory'
import ProductLabel from 'components/ProductLabel'

import { Icon, Fonts } from 'components/base-ui'
import { isShowLabelExcludedFromProtmoion } from 'helpers/product'
import BadgePromotionProduct from 'components/BadgePromotionProduct'

import {
  MemberProductIneligibleReasonEnum,
  ProductMemberTierRankConditionsEnum,
} from 'graphQL/hatostore/generate/operations'
import { ProductType } from './'
import { MembershipProgramPublicType } from 'types/delivery'

import { AppContext } from 'contexts/AppProvider'
import { displayPrice } from 'helpers'
import { BrandTypeEnum, THEMES_UI } from 'brands'
import { checkDineInIsOpen } from 'helpers/dine-in'
import { getResizableURL } from 'helpers/image'
import { useTranslation } from 'react-i18next'

import {
  Card,
  CardThumbnailWrapper,
  CardThumbnail,
  CardName,
  CardPriceSection,
  CardTags,
  CardPriceBeforeDiscount,
  CardPrice,
  BtnAddToCartPlus,
  PlaceholderImage,
  ProductLabelInventoryWrapper,
  ProductLabelWrapper,
  LabelExcludedPromotion,
  BadgePromotionWrapper,
} from './styled'

interface Vendor {
  logoImage?: {
    resizableURL?: string
  }
  nameTh?: string
  nameEn?: string
}

interface Location {
  id?: number
}

interface ProductCardV2Props {
  data: ProductType
  mode?: 'normal' | 'slide'
  thumbnailWidth?: string
  thumbnailHeight?: string
  addToCart?: boolean
  addToCartIconName: string
  onAddToCart: (product: ProductType) => void
  onClickProduct: (product: ProductType) => void
  vendor?: Vendor
  location?: Location
  lazyLoadScrollContainerID?: string
  hideZeroPrice?: boolean
  overflow?: boolean
  membershipProgram?: MembershipProgramPublicType | null
  isOutline?: boolean
  nameCustomClass?: string
  addToCartIconClass?: string
}

const ProductCardV2 = ({
  mode = 'normal',
  addToCart = false,
  addToCartIconName = 'plus-light',
  onAddToCart = (product) => {},
  data,
  vendor,
  lazyLoadScrollContainerID = '',
  overflow = true,
  membershipProgram,
  onClickProduct,
  isOutline = false,
  hideZeroPrice = false,
  nameCustomClass = '',
  addToCartIconClass = '',
}: ProductCardV2Props) => {
  const {
    setIsOpenModalOnlyMemberMenu,
    setIsOpenModalOnlyMemberMenuSpecificCondition,
    setMembershipProgram,
    setArrayOnlyMemberMenuSpecificConditions,
    setMemberMenuIneligibleReason,
    brandType,
    themeUI,
    setVendorImageResizableURLForModalMemberMenu,
    cart,
    isThaiLang,
  } = useContext(AppContext)

  const { t } = useTranslation()

  const productName = isThaiLang ? data.nameTh : data.nameEn

  const getProductImage = (width: string, height: string): string => {
    const fitContainCondition =
      brandType === BrandTypeEnum.THOUSAND_PRODUCTS ||
      brandType === BrandTypeEnum.MALL ||
      brandType === BrandTypeEnum.MALL_WITHOUT_GROCERY

    return getResizableURL(data.images, {
      width,
      height,
      formatType: fitContainCondition ? '_fmt-jpg_fit-contain' : '_fmt-jpg',
    })
  }

  const productImage1x = getProductImage('250', '250')
  const productImage2x = getProductImage('330', '330')
  const productImage3x = getProductImage('375', '375')

  const { isExclusive, isRecommended, tags, tagStr, isEligibleMember, isEligibleBirthMonth } = useMemo(() => {
    // Detect is Exclusive Tags
    let isExclusive = false
    let isRecommended = false
    const tags: string[] = []
    let tagStr = ''
    let isEligibleMember = data.memberTierRankConditions.length > 0
    let isEligibleBirthMonth =
      isEligibleMember &&
      data.memberTierRankConditions.includes(ProductMemberTierRankConditionsEnum.BirthMonth) &&
      !data.memberTierRankConditions.includes(ProductMemberTierRankConditionsEnum.Eligible)
        ? true
        : false

    data.tags?.forEach((tag) => {
      if (tag === 'Exclusive') {
        isExclusive = true
      }

      if (tag === 'Recommended') {
        isRecommended = true
      }

      // if Vegetarian or Vegan
      if (tag === 'Vegetarian' || tag === 'Vegan' || tag === 'Keto' || tag === 'Paleo') {
        tags.push(tag)
      }
    })

    tags.forEach((tag, i) => {
      if (i > 0) {
        tagStr += `, ${tag}`
      } else {
        tagStr = tag
      }
    })

    return {
      isExclusive,
      isRecommended,
      tags,
      tagStr,
      isEligibleMember,
      isEligibleBirthMonth,
    }
  }, [data?.id])

  const isActiveInventory = data?.locationProducts ? data.locationProducts?.[0].inStockQuantity ?? false : false
  const productStock = isActiveInventory !== false ? data.locationProducts?.[0].inStockQuantity : 0
  const isOutOfStock = isActiveInventory !== false && data.locationProducts?.[0].inStockQuantity === 0 ? true : false
  const isMemberItem = isEligibleMember || isEligibleBirthMonth ? true : false

  let labelItem: ReactNode | null = null
  if (
    isEligibleBirthMonth ||
    (isEligibleMember && data.memberIneligibleReason === MemberProductIneligibleReasonEnum.TierBirthMonthRequired)
  ) {
    labelItem = (
      <ProductLabelWrapper>
        <ProductLabel>
          <Icon name="birthday-cake" />{' '}
          <Fonts.SmallTitleBold as="span" className="product-label-text">
            {themeUI === THEMES_UI.GON ? t('tagBirthMonthSet') : t('tagBirthMonthMenu')}
          </Fonts.SmallTitleBold>
        </ProductLabel>
      </ProductLabelWrapper>
    )
  } else if (isEligibleMember) {
    labelItem = (
      <ProductLabelWrapper>
        <ProductLabel>
          <Icon name="star" />{' '}
          <Fonts.SmallTitleBold as="span" className="product-label-text">
            {themeUI === THEMES_UI.GON ? t('tagOnlyMember') : t('tagGenericOnlyMember')}
          </Fonts.SmallTitleBold>
        </ProductLabel>
      </ProductLabelWrapper>
    )
  } else if (isExclusive) {
    labelItem = (
      <ProductLabelWrapper>
        <ProductLabel exclusive>
          <Icon name="star" />{' '}
          <Fonts.SmallTitleBold as="span" className="product-label-text">
            {t('tagExclusive')}
          </Fonts.SmallTitleBold>
        </ProductLabel>
      </ProductLabelWrapper>
    )
  } else if (isRecommended) {
    labelItem = (
      <ProductLabelWrapper>
        <ProductLabel tertiary>
          <Icon name="thumbsup-solid" />{' '}
          <Fonts.SmallTitleBold as="span" className="product-label-text">
            {t('tagRecommended')}
          </Fonts.SmallTitleBold>
        </ProductLabel>
      </ProductLabelWrapper>
    )
  }

  const labelExcludedPromotion =
    (checkDineInIsOpen() && data?.excludedFromPromotion) ||
    isShowLabelExcludedFromProtmoion({ product: data, cart }) ? (
      <LabelExcludedPromotion>{t('productExcludedPromotionShort')}</LabelExcludedPromotion>
    ) : null

  const showCardPrice = !(data.totalPriceSatangs === 0 && hideZeroPrice)

  const CardContent = (
    <>
      <CardThumbnailWrapper
        onClick={() => onClickProduct(data)}
        mode={mode}
        exclusive={isExclusive}
        recommended={isRecommended}
        memberItem={isMemberItem}
      >
        {lazyLoadScrollContainerID ? (
          <LazyLoad
            scrollContainer={`#${lazyLoadScrollContainerID}`}
            overflow={overflow}
            once
            offset={200}
            placeholder={
              <PlaceholderImage
                style={{
                  backgroundImage: `url('${process.env.PUBLIC_URL}/assets/placeholder-product-img.png')`,
                }}
              />
            }
          >
            <CardThumbnail
              exclusive={isExclusive}
              recommended={isRecommended}
              image1x={productImage1x}
              image2x={productImage2x}
              image3x={productImage3x}
            />
          </LazyLoad>
        ) : (
          <CardThumbnail
            exclusive={isExclusive}
            recommended={isRecommended}
            image1x={productImage1x}
            image2x={productImage2x}
            image3x={productImage3x}
          />
        )}

        {labelItem}

        {isActiveInventory !== false && (
          <ProductLabelInventoryWrapper
            themeUI={THEMES_UI.GON}
            isEligibleMember={isEligibleMember && !isEligibleBirthMonth}
          >
            <ProductLabelInventory number={productStock ?? 0} />
          </ProductLabelInventoryWrapper>
        )}

        {data?.promotionBadge && (
          <BadgePromotionWrapper>
            <BadgePromotionProduct promotionStrategyType={data.promotionBadge} />
          </BadgePromotionWrapper>
        )}
      </CardThumbnailWrapper>

      {addToCart ? (
        <>
          <CardPriceSection>
            <div>
              <CardName onClick={() => onClickProduct(data)}>
                <LinesEllipsisLoose className={nameCustomClass} text={productName} maxLine="2" lineHeight="20" />
              </CardName>
              {tags.length > 0 && <CardTags>{tagStr}</CardTags>}
              {showCardPrice && (
                <CardPrice onClick={() => onClickProduct(data)}>
                  {data.discountSatangs > 0 ? (
                    <CardPriceBeforeDiscount>฿{displayPrice(data.priceSatangs / 100)}</CardPriceBeforeDiscount>
                  ) : null}
                  ฿{displayPrice(data.totalPriceSatangs / 100)}
                </CardPrice>
              )}
            </div>

            <BtnAddToCartPlus
              className="button"
              data-testid="add-to-cart-btn"
              hide={isOutOfStock}
              isOutline={isOutline}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                e.currentTarget.classList.remove('touch')
                if (data.memberIneligibleReason === MemberProductIneligibleReasonEnum.MembershipRequired) {
                  setVendorImageResizableURLForModalMemberMenu(vendor?.logoImage?.resizableURL)
                  setIsOpenModalOnlyMemberMenu(true)
                } else if (
                  data.memberIneligibleReason === MemberProductIneligibleReasonEnum.TierBirthMonthRequired ||
                  data.memberIneligibleReason === MemberProductIneligibleReasonEnum.TierIneligible
                ) {
                  setVendorImageResizableURLForModalMemberMenu(vendor?.logoImage?.resizableURL)
                  setArrayOnlyMemberMenuSpecificConditions(data.memberTierRankConditions)
                  setMemberMenuIneligibleReason(data.memberIneligibleReason)
                  setIsOpenModalOnlyMemberMenuSpecificCondition(true)
                  setMembershipProgram(membershipProgram)
                } else {
                  onAddToCart(data)
                }
              }}
            >
              <Icon className={addToCartIconClass} name={addToCartIconName} />
            </BtnAddToCartPlus>
          </CardPriceSection>
          {labelExcludedPromotion}
        </>
      ) : (
        <>
          <CardPriceSection>
            <CardName onClick={() => onClickProduct(data)}>
              <LinesEllipsisLoose className={nameCustomClass} text={productName} maxLine="2" lineHeight="20" />
            </CardName>
            {tags.length > 0 && <CardTags>{tagStr}</CardTags>}
            {showCardPrice && (
              <CardPrice margin={tags.length > 0 ? true : false} onClick={() => onClickProduct(data)}>
                {data.discountSatangs > 0 ? (
                  <CardPriceBeforeDiscount>฿{displayPrice(data.priceSatangs / 100)}</CardPriceBeforeDiscount>
                ) : null}
                ฿{displayPrice(data.totalPriceSatangs / 100)}
              </CardPrice>
            )}
          </CardPriceSection>
          {labelExcludedPromotion}
        </>
      )}
    </>
  )

  return (
    <Card mode={mode} data-testid={productName}>
      {CardContent}
    </Card>
  )
}

export default ProductCardV2
